import type { AudioFileFormat } from './AudioParser';
import { audioFileFormatFromNewFormat } from './AudioParser';
import { lyricsFormatFromNewFormat } from './LyricsParser';
import type { FileFormat } from './SourceParser';
import { fileFormatFromNewFormat } from './SourceParser';
import type { TabFileFormat } from './TabParser';
import { tabFormatFromNewFormat } from './TabParser';

export enum GenericSourceType {
  Audio = 'audio',
  Image = 'image',
  Score = 'score',
  Analysis = 'analysis',
  Tab = 'tab',
  Lyrics = 'lyrics',
  Unknown = 'unknown'
}

export type GenericSource = {
  format : FileFormat | AudioFileFormat | TabFileFormat | undefined,
  type : GenericSourceType,
  parentId : string | undefined
};

const keyTypeMatchTable :
Map<string, GenericSourceType> = new Map<string, GenericSourceType>([
  ['score', GenericSourceType.Score],
  ['image', GenericSourceType.Image],
  ['images', GenericSourceType.Image],
  ['analysis', GenericSourceType.Analysis],
  ['audio', GenericSourceType.Audio],
  ['audio:yt', GenericSourceType.Audio],
  ['video:yt', GenericSourceType.Audio],
  ['tab', GenericSourceType.Tab],
  ['lyrics', GenericSourceType.Lyrics],
]);

export function inferGenericSourceType(source : Map<string, unknown>) : GenericSourceType {
  let types : GenericSourceType[] = Array.from([...source.keys()] as string[])
    .map((key) => keyTypeMatchTable.get(key))
    .filter((key) => key !== undefined)
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    .map((definedKey) => definedKey!) ?? [];

  types = [...new Set(types)]; // remove duplicate
  if (types.length === 1) {
    return types[0];
  }

  return GenericSourceType.Unknown;
}

export function isChild(source : GenericSource) :boolean {
  return typeof source.parentId !== 'undefined';
}

export function convertSourceFormat(sourceJSON:Map<string, unknown>, type : GenericSourceType)
  : AudioFileFormat | FileFormat | TabFileFormat | undefined {
  if (type === GenericSourceType.Image) {
    return fileFormatFromNewFormat(sourceJSON);
  }

  if (type === GenericSourceType.Audio) {
    return audioFileFormatFromNewFormat(sourceJSON);
  }

  if (type === GenericSourceType.Tab) {
    return tabFormatFromNewFormat(sourceJSON);
  }

  if (type === GenericSourceType.Lyrics) {
    return lyricsFormatFromNewFormat(sourceJSON);
  }

  return undefined;
}
